import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import { queryClient } from '../queryClient'

type SortValue = string | number | DateTime | null | undefined

export interface CachedSort<T> {
  /** The column the table is sorted by, named as the table names it. */
  field?: string
  direction?: 'asc' | 'desc'
  /** How to read each sortable column off a row. A column not listed falls back to the default order. */
  accessors: Record<string, (row: T) => SortValue>
  /** The order the server returns rows in when the table has asked for none. */
  fallback?: (row: T) => SortValue
  fallbackDirection?: 'asc' | 'desc'
}

function comparableValue(value: SortValue): string | number | null {
  if (value === null || value === undefined) return null
  if (DateTime.isDateTime(value)) return value.toMillis()
  if (typeof value === 'string' && value.trim() === '') return null
  return value
}

function compare(a: SortValue, b: SortValue): number {
  const left = comparableValue(a)
  const right = comparableValue(b)

  if (left === null) return right === null ? 0 : 1
  if (right === null) return -1
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

export function sortCachedRows<T>(rows: T[], sort?: CachedSort<T>): T[] {
  if (!sort) return rows

  const requested = sort.field ? sort.accessors[sort.field] : undefined
  const accessor = requested ?? sort.fallback
  if (!accessor) return rows

  const direction = requested ? sort.direction ?? 'asc' : sort.fallbackDirection ?? 'asc'
  const sign = direction === 'desc' ? -1 : 1

  return [...rows].sort((a, b) => sign * compare(accessor(a), accessor(b)))
}

export function filterCachedRows<T>(
  unfilteredKeyPrefix: unknown[],
  predicate: (row: T) => boolean,
  sort?: CachedSort<T>,
): PagedResult<T> {
  let widest: PagedResult<T> | undefined
  for (const [, cached] of queryClient.getQueriesData<PagedResult<T>>({ queryKey: unfilteredKeyPrefix })) {
    if ((cached?.data?.length ?? 0) > (widest?.data?.length ?? 0)) widest = cached ?? undefined
  }

  const rows = sortCachedRows((widest?.data ?? []).filter(predicate), sort)
  return new PagedResult<T>(rows, rows.length, Math.max(1, rows.length), 1, Math.max(rows.length, 1))
}

export function matchesText(value: string | undefined | null, filter: string | undefined | null): boolean {
  if (!filter) return true
  return (value ?? '').toLowerCase().includes(filter.toLowerCase())
}
